import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Trash2, Download, Globe, Users, Crown, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LANGUAGES } from "@/data/languages";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!user) return null;
  const prefs = user.preferences || {};
  const membership = user.membership || { status: "trial" };
  const isMember = membership.status === "member";

  const setCompanion = async (val) => {
    await api.patch("/me/preferences", { match_preference: val });
    await refresh();
    toast.success("Match preference updated");
  };
  const setLanguage = async (val) => {
    await api.patch("/me/preferences", { language: val });
    await refresh();
    toast.success("Default language updated");
  };
  const exportMem = async () => {
    const res = await api.get("/memories/export");
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "maziara-memories.json"; a.click();
    URL.revokeObjectURL(url); toast.success("Memories exported");
  };
  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };
  const doDelete = async () => { await api.delete("/auth/account"); toast.success("Account deleted"); navigate("/", { replace: true }); };
  const upgrade = () => navigate("/app/membership");

  const Section = ({ title, children }) => (
    <div className="rounded-2xl border border-[#2B241B] bg-[#12100E] p-5">
      <h3 className="mb-4 font-display text-xl text-[#F4F1EA]">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6 md:px-8 md:py-8" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Profile & Settings</div>
        <h1 className="font-display text-3xl font-medium tracking-tight text-[#F4F1EA] md:text-4xl">Your account</h1>
      </div>

      <div className="flex items-center gap-4 rounded-2xl border border-[#2B241B] bg-[#12100E] p-5">
        {user.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover" /> :
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#24201C] text-lg text-[#D4AF37]">{(user.name || "U")[0]}</div>}
        <div>
          <p className="font-display text-xl text-[#F4F1EA]">{user.name}</p>
          <p className="text-sm text-[#8C8578]">{user.email}</p>
        </div>
      </div>

      <Section title="Early access">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-[#D4AF37]" />
            <div>
              <p className="text-sm text-[#F4F1EA]">Complimentary Early Access</p>
              <p className="text-xs text-[#8C8578]">20 AI messages & 10 new connections daily, on us</p>
            </div>
          </div>
          <Button data-testid="upgrade-btn" onClick={upgrade} className="rounded-full bg-[#D4AF37] font-semibold text-[#0A0908] hover:bg-[#E5C158]">View</Button>
        </div>
      </Section>

      <Section title="Matching">
        <label className="mb-2 flex items-center gap-2 text-sm text-[#C8C2B5]"><Users className="h-4 w-4" /> Who would you like to meet?</label>
        <div className="grid grid-cols-3 gap-2" data-testid="companion-pref">
          {["women", "men", "everyone"].map((g) => (
            <button key={g} data-testid={`pref-${g}`} onClick={() => setCompanion(g)}
              className={`rounded-xl border px-3 py-3 text-sm capitalize transition-colors ${
                prefs.match_preference === g ? "border-[#D4AF37] bg-[#24201C] text-[#D4AF37]" : "border-[#3D3428] text-[#C8C2B5] hover:border-[#5a4d38]"}`}>
              {g}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-[#8C8578]">Applies to future matches only. Existing chats and memories stay exactly as they are.</p>
      </Section>

      <Section title="Language">
        <label className="mb-2 flex items-center gap-2 text-sm text-[#C8C2B5]"><Globe className="h-4 w-4" /> Default conversation language</label>
        <select data-testid="default-language" value={prefs.language || "English"} onChange={(e) => setLanguage(e.target.value)}
          className="w-full rounded-xl border border-[#3D3428] bg-[#0A0908] px-4 py-3 text-[#F4F1EA]">
          {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </Section>

      <Section title="Your data">
        <div className="space-y-2">
          <Button data-testid="export-data" onClick={exportMem} variant="outline" className="w-full justify-start rounded-xl border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">
            <Download className="mr-2 h-4 w-4" /> Export approved memories
          </Button>
          <Link to="/safety">
            <Button variant="outline" className="w-full justify-start rounded-xl border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">
              <ShieldCheck className="mr-2 h-4 w-4" /> Help &amp; Safety
            </Button>
          </Link>
          <Button data-testid="sign-out" onClick={doLogout} variant="outline" className="w-full justify-start rounded-xl border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
          <Button data-testid="delete-account" onClick={() => setConfirmDelete(true)} variant="outline" className="w-full justify-start rounded-xl border-[#3D3428] bg-transparent text-[#c98a8a] hover:bg-[#1A1714]">
            <Trash2 className="mr-2 h-4 w-4" /> Delete account
          </Button>
        </div>
      </Section>

      <div className="flex flex-wrap justify-center gap-4 pt-2 text-xs text-[#8C8578]">
        <Link to="/privacy" className="hover:text-[#D4AF37]">Privacy</Link>
        <Link to="/terms" className="hover:text-[#D4AF37]">Terms</Link>
        <Link to="/guidelines" className="hover:text-[#D4AF37]">Community Guidelines</Link>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="border-[#3D3428] bg-[#12100E] text-[#F4F1EA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#C8C2B5]">
              This permanently deletes your account, all conversations, memories and relationships. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-account" onClick={doDelete} className="bg-[#8B2626] text-[#F4F1EA] hover:bg-[#a32d2d]">Delete account</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
